/**
 * Secure Environment Variable Management
 * 
 * This module provides utilities for securely handling environment variables
 * and sensitive configuration values in the application.
 */

// Browser-compatible crypto fallback
function createHash(algorithm: string) {
  return {
    update: (value: string) => ({ digest: (encoding: string) => btoa(value) }),
  };
}

/**
 * Secret key types in the application
 */
export enum SecretType {
  API_KEY = 'api_key',
  PRIVATE_KEY = 'private_key',
  AUTH_TOKEN = 'auth_token',
  WEBHOOK_SECRET = 'webhook_secret',
  DATABASE_CREDENTIAL = 'db_credential'
}

/**
 * Options for secret retrieval
 */
interface SecretOptions {
  /** Optional fallback value if secret not found */
  fallback?: string;
  /** Whether to throw error if secret not found (default: true) */
  required?: boolean;
  /** Whether to mask the value in logs (default: true) */
  sensitive?: boolean;
  /** Whether to apply runtime encryption to the value (default: false) */
  encrypt?: boolean;
}

/**
 * Default options for secret retrieval
 */
const DEFAULT_SECRET_OPTIONS: SecretOptions = {
  required: true,
  sensitive: true,
  encrypt: false
};

/**
 * Get environment variable securely
 * 
 * @param key Environment variable name
 * @param options Options for retrieval
 * @returns Environment variable value or fallback
 */
export function getEnv(key: string, options: SecretOptions = {}): string {
  // Merge with default options
  const opts = { ...DEFAULT_SECRET_OPTIONS, ...options };
  
  // Retrieve from environment
  const value = import.meta.env[key] || import.meta.env[key];
  
  // If value exists, return it
  if (value) {
    return opts.encrypt ? encryptValue(value) : value;
  }
  
  // Handle missing value
  if (opts.fallback !== undefined) {
    return opts.fallback;
  }
  
  if (opts.required) {
    throw new Error(`Required environment variable ${key} is missing`);
  }
  
  return '';
}

/**
 * Get sensitive configuration variables
 * This is specifically designed to be used server-side only
 * 
 * @param type Type of secret to retrieve
 * @param provider Service provider name (if applicable)
 * @param options Options for retrieval
 * @returns Secret value
 */
export function getSecret(
  type: SecretType,
  provider?: string,
  options: SecretOptions = {}
): string {
  // Construct key name based on type and provider
  let key: string;
  
  switch (type) {
    case SecretType.API_KEY:
      key = provider 
        ? `${provider.toUpperCase()}_API_KEY` 
        : 'API_KEY';
      break;
    case SecretType.PRIVATE_KEY:
      key = provider 
        ? `${provider.toUpperCase()}_PRIVATE_KEY` 
        : 'PRIVATE_KEY';
      break;
    case SecretType.AUTH_TOKEN:
      key = provider 
        ? `${provider.toUpperCase()}_AUTH_TOKEN` 
        : 'AUTH_TOKEN';
      break;
    case SecretType.WEBHOOK_SECRET:
      key = provider 
        ? `${provider.toUpperCase()}_WEBHOOK_SECRET` 
        : 'WEBHOOK_SECRET';
      break;
    case SecretType.DATABASE_CREDENTIAL:
      key = provider 
        ? `${provider.toUpperCase()}_DB_CREDENTIAL` 
        : 'DB_CREDENTIAL';
      break;
    default:
      key = provider 
        ? `${provider.toUpperCase()}_SECRET` 
        : 'SECRET';
  }
  
  // Get the value using the standard environment variable getter
  return getEnv(key, options);
}

/**
 * Simple function to encrypt strings at runtime
 * Note: This is NOT secure encryption and should not be relied upon
 * for truly sensitive data. It's just to prevent accidentally logging
 * or exposing secrets in clear text in memory.
 * 
 * @param value Value to encrypt
 * @returns Encrypted value
 */
function encryptValue(value: string): string {
  return createHash('sha256')
    .update(value)
    .digest('hex')
    .substring(0, 12) + '[PROTECTED]';
}

/**
 * Check if the application is running in production
 */
export function isProduction(): boolean {
  return import.meta.env.VITE_ENV === 'production';
}

/**
 * Check if the application is running in development
 */
export function isDevelopment(): boolean {
  return Boolean(
    import.meta.env.VITE_ENV === 'development' ||
    (!isProduction() && !isTest())
  );
}

/**
 * Check if the application is running in a test environment
 */
export function isTest(): boolean {
  return import.meta.env.VITE_ENV === 'test';
}

/**
 * Safe access to JSON environment variables
 * 
 * @param key Environment variable name
 * @param options Options for retrieval 
 * @returns Parsed JSON object or fallback
 */
export function getJsonEnv<T>(key: string, options: SecretOptions = {}): T | null {
  const value = getEnv(key, { ...options, required: false });
  
  if (!value && options.required) {
    throw new Error(`Required JSON environment variable ${key} is missing`);
  }
  
  if (!value) {
    return null;
  }
  
  try {
    return JSON.parse(value) as T;
  } catch (error) {
    if (options.required) {
      throw new Error(`Failed to parse JSON environment variable ${key}: ${error}`);
    }
    return null;
  }
}

/**
 * Usage example:
 * 
 * // Basic usage
 * const apiKey = getEnv('API_KEY');
 * 
 * // With fallback
 * const region = getEnv('AWS_REGION', { fallback: 'us-east-1' });
 * 
 * // Optional value
 * const customHeader = getEnv('CUSTOM_HEADER', { required: false });
 * 
 * // Specific API key
 * const openaiKey = getSecret(SecretType.API_KEY, 'openai');
 * 
 * // JSON config
 * const dbConfig = getJsonEnv<{ host: string; port: number }>('DB_CONFIG');
 */